import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { NewReportingFilter, ReportConfig } from '../interfaces/new-reporting-filter';

export interface SpecialCaseData {
  deChannels?: number[];
  minutes?: number;
  ioChannels?: Record<string, string>;
}

@Injectable({
  providedIn: 'root'
})
export class SpecialCasesService {

  // Observable für Special Cases State
  private specialCasesData$ = new BehaviorSubject<SpecialCaseData>({});

  constructor() {}

  /**
   * Get Special Cases Data as Observable
   */
  getSpecialCasesData$(): Observable<SpecialCaseData> {
    return this.specialCasesData$.asObservable();
  }

  /**
   * Update Special Cases Data
   */
  updateSpecialCasesData(data: SpecialCaseData): void {
    this.specialCasesData$.next(data);
  }

  /**
   * Reset Special Cases
   */
  resetSpecialCases(): void {
    this.specialCasesData$.next({});
  }

  /**
   * Add deChannels (Traffic Data Raw)
   */
  addDeChannel(channelId: number): void {
    const current = this.specialCasesData$.value;
    const channels = current.deChannels || [];
    if (!channels.includes(channelId)) {
      channels.push(channelId);
    }
    this.specialCasesData$.next({
      ...current,
      deChannels: channels
    });
  }

  /**
   * Remove deChannel
   */
  removeDeChannel(channelId: number): void {
    const current = this.specialCasesData$.value;
    const channels = (current.deChannels || []).filter(ch => ch !== channelId);
    this.specialCasesData$.next({
      ...current,
      deChannels: channels
    });
  }

  /**
   * Set minutes value
   */
  setMinutes(minutes: number): void {
    const current = this.specialCasesData$.value;
    this.specialCasesData$.next({
      ...current,
      minutes
    });
  }

  /**
   * Add ioChannel
   */
  addIoChannel(key: string, value: string): void {
    const current = this.specialCasesData$.value;
    const ioChannels = current.ioChannels || {};
    ioChannels[key] = value;
    this.specialCasesData$.next({
      ...current,
      ioChannels
    });
  }

  /**
   * Remove ioChannel
   */
  removeIoChannel(key: string): void {
    const current = this.specialCasesData$.value;
    const ioChannels = { ...current.ioChannels };
    delete ioChannels[key];
    this.specialCasesData$.next({
      ...current,
      ioChannels: Object.keys(ioChannels).length > 0 ? ioChannels : undefined
    });
  }

  /**
   * Merge Special Cases mit Filter
   */
  mergeWithFilter(filter: NewReportingFilter, config: ReportConfig): NewReportingFilter {
    const current = this.specialCasesData$.value;
    const merged = { ...filter };

    // Merge deChannels
    if (config.hasDeChannels && current.deChannels && current.deChannels.length > 0) {
      merged.deChannels = current.deChannels;
    }

    // Merge minutes
    if (config.hasMinutes && current.minutes) {
      merged.minutes = current.minutes;
    } else if (config.hasMinutes && config.minutesDefault) {
      merged.minutes = config.minutesDefault;
    }

    // Merge ioChannels
    if (config.hasIoChannels && current.ioChannels && Object.keys(current.ioChannels).length > 0) {
      merged.ioChannels = current.ioChannels;
    }

    return merged;
  }

  /**
   * Überprüfe ob Report Special Cases hat
   */
  hasSpecialCases(config: ReportConfig): boolean {
    return !!(config.hasDeChannels || config.hasMinutes || config.hasIoChannels);
  }

  /**
   * Get Special Cases für Report
   */
  getSpecialCasesForReport(config: ReportConfig): {
    hasDeChannels: boolean;
    hasMinutes: boolean;
    hasIoChannels: boolean;
  } {
    return {
      hasDeChannels: config.hasDeChannels || false,
      hasMinutes: config.hasMinutes || false,
      hasIoChannels: config.hasIoChannels || false
    };
  }
}
